import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Wrench, Play, CheckCircle, Package, AlertCircle, 
  Clock, FileText, Upload, XCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepairManagement = ({ user }) => {
  const { device_id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [repairNotes, setRepairNotes] = useState('');
  const [partsNeeded, setPartsNeeded] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const [berNrReason, setBerNrReason] = useState('');
  const [berNrType, setBerNrType] = useState('ber');

  useEffect(() => {
    loadDevice();
  }, [device_id]);

  const loadDevice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/trc/devices/${device_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDevice(response.data.device);
      }
    } catch (error) {
      console.error('Error loading device:', error);
      alert('Device not found');
      navigate('/engineer/devices');
    } finally {
      setLoading(false);
    }
  };

  const handleStartRepair = async () => {
    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/${device_id}/repair-start`,
        { engineer_id: engineerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Repair started!');
        loadDevice();
        setAction(null);
      }
    } catch (error) {
      console.error('Error starting repair:', error);
      alert('Error starting repair');
    }
  };

  const handleCompleteRepair = async () => {
    if (!repairNotes.trim()) {
      alert('Please add repair notes');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/${device_id}/repair-complete`,
        {
          engineer_id: engineerId,
          repair_notes: repairNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Repair completed! Time: ${response.data.repair_time_minutes?.toFixed(1)} minutes`);
        loadDevice();
        setAction(null);
        setRepairNotes('');
      }
    } catch (error) {
      console.error('Error completing repair:', error);
      alert('Error completing repair');
    }
  };

  const handleRequestParts = async () => {
    if (!partsNeeded.trim()) {
      alert('Please enter parts needed');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      const partsList = partsNeeded.split(',').map(p => p.trim()).filter(p => p);
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/${device_id}/request-parts`,
        {
          engineer_id: engineerId,
          parts_needed: partsList
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert('Additional parts requested (RE-PNA)');
        loadDevice();
        setAction(null);
        setPartsNeeded('');
      }
    } catch (error) {
      console.error('Error requesting parts:', error);
      alert('Error requesting parts');
    }
  };

  const handleMarkBERNR = async () => {
    if (!berNrReason.trim()) {
      alert('Please provide a reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const engineerId = user?.employee_id || 'ENG001';
      
      const response = await axios.post(
        `${API_URL}/api/trc/devices/${device_id}/mark-ber-nr`,
        {
          engineer_id: engineerId,
          status: berNrType,
          reason: berNrReason,
          video_url: null // TODO: Implement video upload
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(response.data.message);
        loadDevice();
        setAction(null);
        setBerNrReason('');
      }
    } catch (error) {
      console.error('Error marking BER/NR:', error);
      alert('Error marking device');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading device...</p>
        </div>
      </div>
    );
  }

  if (!device) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wrench className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Repair Management</h1>
          </div>
          <button
            onClick={() => navigate('/engineer/devices')}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Back to Devices
          </button>
        </div>

        {/* Device Info Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{device.did}</h2>
              <p className="text-lg text-gray-600">{device.brand} {device.model}</p>
              <p className="text-sm text-gray-500">{device.trc_device_id}</p>
            </div>
            {device.is_sos && (
              <span className="px-4 py-2 bg-red-100 text-red-800 rounded-lg font-bold flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                SOS PRIORITY
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-gray-600">Status</span>
              <div className="mt-1">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-semibold">
                  {device.status.replace(/_/g, ' ').toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-600">IQC Grade</span>
              <p className="font-semibold text-gray-900 mt-1">{device.iqc_grade || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Category</span>
              <p className="font-semibold text-gray-900 mt-1">{device.category}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Bin Location</span>
              <p className="font-semibold text-gray-900 mt-1">{device.current_bin || 'Not assigned'}</p>
            </div>
          </div>

          {device.customer_complaint && (
            <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <strong className="text-sm text-orange-900">Customer Complaint:</strong>
              <p className="text-sm text-orange-800 mt-1">{device.customer_complaint}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {device.status === 'rfr' && (
            <button
              onClick={() => setAction('start')}
              className="py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Start Repair
            </button>
          )}
          
          {device.status === 'repair_in_progress' && (
            <>
              <button
                onClick={() => setAction('complete')}
                className="py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Complete Repair
              </button>
              <button
                onClick={() => setAction('parts')}
                className="py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 flex items-center justify-center gap-2"
              >
                <Package className="w-5 h-5" />
                Request Parts
              </button>
            </>
          )}

          <button
            onClick={() => setAction('ber-nr')}
            className="py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 flex items-center justify-center gap-2"
          >
            <XCircle className="w-5 h-5" />
            Mark BER/NR
          </button>
        </div>

        {/* Action Modals */}
        {action === 'start' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Start Repair</h3>
            <p className="text-gray-600 mb-4">
              Are you ready to start repairing this device? The repair timer will begin.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartRepair}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                Start Now
              </button>
            </div>
          </div>
        )}

        {action === 'complete' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Complete Repair</h3>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Repair Notes *</label>
            <textarea
              value={repairNotes}
              onChange={(e) => setRepairNotes(e.target.value)}
              rows="4"
              placeholder="Describe the repairs done, parts replaced, tests performed..."
              className="w-full px-4 py-3 border-2 rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteRepair}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                Mark as Completed
              </button>
            </div>
          </div>
        )}

        {action === 'parts' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Request Additional Parts (RE-PNA)</h3>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Parts Needed (comma separated) *</label>
            <input
              type="text"
              value={partsNeeded}
              onChange={(e) => setPartsNeeded(e.target.value)}
              placeholder="e.g., Display, Battery, Camera Module"
              className="w-full px-4 py-3 border-2 rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestParts}
                className="flex-1 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
              >
                Request Parts
              </button>
            </div>
          </div>
        )}

        {action === 'ber-nr' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Mark as BER / NR</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
              <select
                value={berNrType}
                onChange={(e) => setBerNrType(e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-lg"
              >
                <option value="ber">BER (Beyond Economic Repair)</option>
                <option value="nr">NR (Non-Repairable)</option>
              </select>
            </div>

            <label className="block text-sm font-semibold text-gray-700 mb-2">Reason *</label>
            <textarea
              value={berNrReason}
              onChange={(e) => setBerNrReason(e.target.value)}
              rows="4"
              placeholder="Explain why this device cannot be repaired..."
              className="w-full px-4 py-3 border-2 rounded-lg mb-4"
            />

            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-900">
                <strong>Note:</strong> This will require L4 Engineer approval. Please ensure you have recorded a video proof.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setAction(null)}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkBERNR}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                Submit for Approval
              </button>
            </div>
          </div>
        )}

        {/* Device History */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Device History
          </h3>
          {device.history && device.history.length > 0 ? (
            <div className="space-y-3">
              {device.history.slice().reverse().map((entry, index) => (
                <div key={index} className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">{entry.action.replace(/_/g, ' ').toUpperCase()}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                    <p className="text-xs text-gray-500 mt-1">By: {entry.by}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No history available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepairManagement;
